import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/*
 * Cookie/tracking consent.
 *
 * Only one optional purpose exists today: marketing (the Meta Pixel).
 * Everything else the app stores — auth token, cart, language preference —
 * is strictly necessary or user-requested, so it needs no consent and is
 * deliberately not gated here.
 *
 * The stored choice carries a version. Bump CONSENT_VERSION whenever the
 * purposes change; old decisions stop counting and users are asked again,
 * because consent given for one set of purposes doesn't cover a new one.
 */

export const CONSENT_STORAGE_KEY = 'cookieConsent';
export const CONSENT_VERSION = 1;

export interface ConsentValue {
  marketing: boolean;
  version: number;
  decidedAt: string;
}

interface ConsentContextType {
  /** null means the visitor has not decided yet — treat as "no consent". */
  consent: ConsentValue | null;
  hasDecided: boolean;
  isBannerOpen: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  /** Re-opens the banner so a decision can be changed or withdrawn. */
  openBanner: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

const readStoredConsent = (): ConsentValue | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    // A decision made against a different set of purposes no longer applies.
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;

    return {
      marketing: parsed.marketing === true,
      version: CONSENT_VERSION,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : '',
    };
  } catch {
    // Corrupt value or storage unavailable — safest reading is "no consent".
    return null;
  }
};

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentValue | null>(readStoredConsent);
  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(() => readStoredConsent() === null);

  const decide = useCallback((marketing: boolean) => {
    const value: ConsentValue = {
      marketing,
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Private mode or blocked storage: honour the choice for this session
      // rather than failing, and we'll simply ask again next visit.
    }

    setConsent(value);
    setIsBannerOpen(false);
  }, []);

  const acceptAll = useCallback(() => decide(true), [decide]);
  const rejectAll = useCallback(() => decide(false), [decide]);
  const openBanner = useCallback(() => setIsBannerOpen(true), []);

  const value = useMemo(
    () => ({
      consent,
      hasDecided: consent !== null,
      isBannerOpen,
      acceptAll,
      rejectAll,
      openBanner,
    }),
    [consent, isBannerOpen, acceptAll, rejectAll, openBanner]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

/*
 * Runs `onGrant` / `onRevoke` as the marketing decision changes. Kept here
 * so the pixel wiring lives next to the consent state it depends on.
 */
export const useMarketingConsentEffect = (onGrant: () => void, onRevoke: () => void) => {
  const { consent } = useConsent();

  useEffect(() => {
    if (consent === null) return; // undecided: stay dormant
    if (consent.marketing) onGrant();
    else onRevoke();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);
};
